import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    MessageSquare,
    Mail,
    Phone,
    Calendar,
    Eye,
    Trash2,
    RefreshCw,
    FileText,
    User,
    CheckCircle,
    Circle,
    Filter,
    Search
} from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, where, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../components/ui/toast';

const MESSAGES_KEY = 'siteMessages';

// Helper to get messages from localStorage (backup)
const getLocalMessages = () => {
    try {
        const stored = localStorage.getItem(MESSAGES_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Error reading messages:', e);
        return [];
    }
};

// Helper to save messages to localStorage (backup)
const saveLocalMessages = (messages) => {
    try {
        localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
        return true;
    } catch (e) {
        console.error('Error saving messages:', e);
        return false;
    }
};

const MessagesPage = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [filter, setFilter] = useState('all'); // all, unread, contact, application
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const toast = useToast();

    useEffect(() => {
        fetchMessages();

        // Real-time updates
        const interval = setInterval(fetchMessages, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchMessages = async () => {
        setSelectedIds([]); // Reset selection on refresh
        try {
            let allMessages = [];

            // Try Firestore first (primary)
            try {
                const q = query(collection(db, 'messages'), orderBy('created_at', 'desc'));
                const snapshot = await getDocs(q);
                allMessages = snapshot.docs.map(docSnap => ({
                    ...docSnap.data(),
                    id: docSnap.id,
                    firestore_id: docSnap.id
                }));
                console.log('✅ Loaded', allMessages.length, 'messages from Firestore');

                // Clear localStorage cache since we have Firestore data
                // This prevents duplicate buildup in localStorage
                saveLocalMessages([]);
            } catch (firestoreError) {
                console.warn('⚠️ Firestore read failed, using localStorage:', firestoreError.message);
                allMessages = getLocalMessages();
            }

            // Sort by date descending
            allMessages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setMessages(allMessages);
        } catch (error) {
            console.error('Error fetching messages:', error);
            setMessages(getLocalMessages());
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        const updatedMessages = messages.map(msg =>
            msg.id === id ? { ...msg, read: true } : msg
        );
        setMessages(updatedMessages);
        saveLocalMessages(updatedMessages);

        // Also update in Firestore
        try {
            const msg = messages.find(m => m.id === id);
            if (msg?.firestore_id) {
                await updateDoc(doc(db, 'messages', msg.firestore_id), { read: true });
                console.log('✅ Message marked as read in Firestore');
            }
        } catch (error) {
            console.warn('Could not update Firestore:', error.message);
        }

        toast.success('Message marked as read');
    };

    const deleteMessage = async (id) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
            const msg = messages.find(m => m.id === id);
            const updatedMessages = messages.filter(msg => msg.id !== id);
            setMessages(updatedMessages);
            saveLocalMessages(updatedMessages);
            setSelectedMessage(null);
            setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));

            // Also delete from Firestore
            try {
                if (msg?.firestore_id) {
                    await deleteDoc(doc(db, 'messages', msg.firestore_id));
                    console.log('✅ Message deleted from Firestore');
                }
            } catch (error) {
                console.warn('Could not delete from Firestore:', error.message);
            }

            toast.success('Message deleted');
        }
    };

    const deleteSelectedMessages = async () => {
        if (selectedIds.length === 0) return;

        if (window.confirm(`Are you sure you want to delete ${selectedIds.length} message(s)?`)) {
            setLoading(true);
            const updatedMessages = messages.filter(msg => !selectedIds.includes(msg.id));

            try {
                const batch = writeBatch(db);
                let deletedCount = 0;

                selectedIds.forEach(id => {
                    const msg = messages.find(m => m.id === id);
                    if (msg?.firestore_id) {
                        batch.delete(doc(db, 'messages', msg.firestore_id));
                        deletedCount++;
                    }
                });

                if (deletedCount > 0) {
                    await batch.commit();
                    console.log(`✅ ${deletedCount} messages deleted from Firestore`);
                }

                setMessages(updatedMessages);
                saveLocalMessages(updatedMessages);
                setSelectedIds([]);
                if (selectedMessage && selectedIds.includes(selectedMessage.id)) {
                    setSelectedMessage(null);
                }
                toast.success(`${selectedIds.length} message(s) deleted`);
            } catch (error) {
                console.error('Error deleting messages:', error);
                toast.error('Failed to delete some messages');
            } finally {
                setLoading(false);
            }
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredMessages.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredMessages.map(m => m.id));
        }
    };

    const toggleSelect = (e, id) => {
        e.stopPropagation();
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const markAllAsRead = async () => {
        const updatedMessages = messages.map(msg => ({ ...msg, read: true }));
        setMessages(updatedMessages);
        saveLocalMessages(updatedMessages);

        // Also update in Firestore
        try {
            for (const msg of messages) {
                if (msg.firestore_id && !msg.read) {
                    await updateDoc(doc(db, 'messages', msg.firestore_id), { read: true });
                }
            }
            console.log('✅ All messages marked as read in Firestore');
        } catch (error) {
            console.warn('Could not update all in Firestore:', error.message);
        }

        toast.success('All messages marked as read');
    };

    const filteredMessages = messages.filter(msg => {
        const matchesFilter =
            filter === 'all' ? true :
                filter === 'unread' ? !msg.read :
                    filter === 'contact' ? msg.type === 'contact' :
                        filter === 'application' ? msg.type === 'application' : true;

        const matchesSearch =
            msg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            msg.message?.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesFilter && matchesSearch;
    });

    const unreadCount = messages.filter(m => !m.read).length;

    return (
        <AdminLayout title="Messages">
            <Helmet>
                <title>Messages - Admin Dashboard</title>
            </Helmet>

            {/* Stats Bar */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                            <MessageSquare className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{messages.length}</p>
                            <p className="text-sm text-gray-400">Total Messages</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center">
                            <Circle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{unreadCount}</p>
                            <p className="text-sm text-gray-400">Unread</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                            <Mail className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{messages.filter(m => m.type === 'contact').length}</p>
                            <p className="text-sm text-gray-400">Contacts</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                            <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{messages.filter(m => m.type === 'application').length}</p>
                            <p className="text-sm text-gray-400">Applications</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Messages List */}
                <div className="flex-1 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
                    {/* Toolbar */}
                    <div className="p-4 border-b border-white/10 space-y-4">
                        <div className="flex flex-wrap gap-3 items-center justify-between">
                            <div className="flex gap-2">
                                {['all', 'unread', 'contact', 'application'].map(f => (
                                    <button
                                        key={f}
                                        onClick={() => setFilter(f)}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === f
                                            ? 'bg-teal-500 text-white'
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                                            }`}
                                    >
                                        {f === 'all' ? 'All' : f === 'unread' ? 'Unread' : f === 'contact' ? 'Contacts' : 'Applications'}
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                {selectedIds.length > 0 && (
                                    <button
                                        onClick={deleteSelectedMessages}
                                        className="px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg text-sm transition-all flex items-center gap-2"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete ({selectedIds.length})
                                    </button>
                                )}
                                <button
                                    onClick={markAllAsRead}
                                    className="px-3 py-1.5 bg-white/5 text-gray-400 hover:bg-white/10 rounded-lg text-sm transition-all"
                                >
                                    Mark all read
                                </button>
                                <button
                                    onClick={fetchMessages}
                                    className="p-2 bg-white/5 text-gray-400 hover:bg-white/10 rounded-lg transition-all"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="flex gap-4 items-center">
                            <button
                                onClick={toggleSelectAll}
                                className="flex items-center gap-2 px-3 py-1.5 bg-white/5 text-gray-400 hover:bg-white/10 rounded-lg text-xs transition-all"
                            >
                                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${selectedIds.length === filteredMessages.length && filteredMessages.length > 0
                                        ? 'bg-teal-500 border-teal-500'
                                        : 'border-white/20'
                                    }`}>
                                    {selectedIds.length === filteredMessages.length && filteredMessages.length > 0 && (
                                        <CheckCircle className="w-3 h-3 text-white" />
                                    )}
                                </div>
                                <span>{selectedIds.length === filteredMessages.length && filteredMessages.length > 0 ? 'Deselect All' : 'Select All'}</span>
                            </button>
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search messages..."
                                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Messages List */}
                    <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
                        {loading ? (
                            <div className="p-8 text-center text-gray-400">
                                <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                Loading messages...
                            </div>
                        ) : filteredMessages.length > 0 ? (
                            filteredMessages.map(msg => (
                                <div
                                    key={msg.id}
                                    onClick={() => {
                                        setSelectedMessage(msg);
                                        if (!msg.read) markAsRead(msg.id);
                                    }}
                                    className={`p-4 cursor-pointer transition-all hover:bg-white/5 flex items-center gap-3 ${selectedMessage?.id === msg.id ? 'bg-white/10' : ''
                                        } ${!msg.read ? 'border-l-4 border-teal-500' : ''}`}
                                >
                                    {/* Checkbox */}
                                    <div
                                        onClick={(e) => toggleSelect(e, msg.id)}
                                        className="flex-shrink-0"
                                    >
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${selectedIds.includes(msg.id)
                                                ? 'bg-teal-500 border-teal-500'
                                                : 'border-white/20 hover:border-teal-500/50'
                                            }`}>
                                            {selectedIds.includes(msg.id) && (
                                                <CheckCircle className="w-3 h-3 text-white" />
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${msg.type === 'application'
                                            ? 'bg-purple-500/20 text-purple-400'
                                            : 'bg-teal-500/20 text-teal-400'
                                            }`}>
                                            {msg.type === 'application' ? <FileText className="w-5 h-5" /> : <User className="w-5 h-5" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <p className={`font-medium truncate ${msg.read ? 'text-gray-300' : 'text-white'}`}>
                                                    {msg.name || 'Unknown'}
                                                </p>
                                                <span className="text-xs text-gray-500">
                                                    {new Date(msg.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-400 truncate">{msg.subject || msg.productTitle || 'No subject'}</p>
                                            <p className="text-xs text-gray-500 truncate mt-1">{msg.message || msg.email}</p>
                                        </div>
                                        {!msg.read && (
                                            <div className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0"></div>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-8 text-center">
                                <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                                <p className="text-gray-400">No messages found</p>
                                <p className="text-sm text-gray-500 mt-1">Messages from the contact form will appear here</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Message Detail */}
                {selectedMessage && (
                    <div className="lg:w-96 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${selectedMessage.type === 'application'
                                ? 'bg-purple-500/20 text-purple-300'
                                : 'bg-teal-500/20 text-teal-300'
                                }`}>
                                {selectedMessage.type === 'application' ? 'Application' : 'Contact'}
                            </span>
                            <button
                                onClick={() => deleteMessage(selectedMessage.id)}
                                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs text-gray-500 uppercase tracking-wider">From</label>
                                <p className="text-white font-medium">{selectedMessage.name}</p>
                            </div>

                            <div>
                                <label className="text-xs text-gray-500 uppercase tracking-wider">Email</label>
                                <p className="text-gray-300">{selectedMessage.email}</p>
                            </div>

                            {selectedMessage.phone && (
                                <div>
                                    <label className="text-xs text-gray-500 uppercase tracking-wider">Phone</label>
                                    <p className="text-gray-300">{selectedMessage.phone}</p>
                                </div>
                            )}

                            {selectedMessage.subject && (
                                <div>
                                    <label className="text-xs text-gray-500 uppercase tracking-wider">Subject</label>
                                    <p className="text-gray-300">{selectedMessage.subject}</p>
                                </div>
                            )}

                            {selectedMessage.productTitle && (
                                <div>
                                    <label className="text-xs text-gray-500 uppercase tracking-wider">Product</label>
                                    <p className="text-gray-300">{selectedMessage.productTitle}</p>
                                </div>
                            )}

                            <div>
                                <label className="text-xs text-gray-500 uppercase tracking-wider">Message</label>
                                <p className="text-gray-300 whitespace-pre-wrap">{selectedMessage.message || 'No message content'}</p>
                            </div>

                            {selectedMessage.type === 'application' && (
                                <div className="pt-4 border-t border-white/10">
                                    <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Application Details</label>
                                    <div className="space-y-2 text-sm">
                                        {selectedMessage.nic && <p className="text-gray-400">NIC: <span className="text-white">{selectedMessage.nic}</span></p>}
                                        {selectedMessage.income && <p className="text-gray-400">Monthly Income: <span className="text-white">LKR {selectedMessage.income}</span></p>}
                                        {selectedMessage.loanAmount && <p className="text-gray-400">Requested Amount: <span className="text-white">LKR {selectedMessage.loanAmount}</span></p>}
                                        {selectedMessage.tenure && <p className="text-gray-400">Tenure: <span className="text-white">{selectedMessage.tenure} months</span></p>}
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 border-t border-white/10">
                                <label className="text-xs text-gray-500 uppercase tracking-wider">Received</label>
                                <p className="text-gray-300">
                                    {new Date(selectedMessage.created_at).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default MessagesPage;
